import { createApi } from '@reduxjs/toolkit/query/react'
import customOpwFetchBase from './customOpwFetchBase'
import { GenericResponse } from '../../types'
import {
  BrandList,
  CountryList,
  ISkuItem,
  SaleIntegrity,
  StateList,
} from '../../types/SaleIntegrity'
import { method } from 'lodash'

interface IReprocessRequest {
  dealerSalesData: SaleIntegrity
}

interface ICountryListResponse {
  getCountryListData: CountryList
}
interface IStateListResponse {
  getStateListData: StateList
}
interface IBrandListResponse {
  getBrandListData: BrandList
}

interface IReprocessResponse {
  status: number
  message: string
  data?: any
}
interface IBulkUpdateRequest {
  dealerSalesIds: number[]
  dealerGroupNumber?: string
  dealerId?: string
  sku?: string
}

interface IBulkUpdateResponse {
  status: 'success' | 'partial' | 'error'
  message: string
  data?: {
    success: number[]
    failed: number[]
  }
}
interface IBulkDeleteRequest {
  dealerSalesIds: number[]
}
interface IBulkDeleteResponse {
  status: 'success' | 'partial' | 'error'
  message: string
  data?: {
    success: number[]
    failed: number[]
  }
}
export const SaleIntegrityApi = createApi({
  reducerPath: 'SaleIntegrityApi',
  baseQuery: customOpwFetchBase,
  tagTypes: ['SaleIntegrity'],
  endpoints: (builder) => ({
    getDealerSale: builder.query<SaleIntegrity[], void>({
      query() {
        return {
          url: 'dealerSale/get',
          method: 'GET',
          credentials: 'include',
        }
      },
      transformResponse: (response: { status: string; data: SaleIntegrity[] }) => {
        return response.data
      },
      providesTags: ['SaleIntegrity'],
    }),
    //  Get Country List
    getCountryList: builder.query<CountryList[], void>({
      query: () => ({
        url: 'getCountryList',
        method: 'GET',
        credentials: 'include',
      }),
      transformResponse: (response: any) => {
        const data = response?.data?.items
        return Array.isArray(data) ? data : []
      },
    }),
    //  Get state List
    getStateList: builder.query<StateList[], number>({
      query: (countryId) => ({
        url: 'getStateList',
        method: 'GET',
        params: { countryId },
        credentials: 'include',
      }),
      transformResponse: (response: any) => {
        const data = response?.data?.items
        return Array.isArray(data) ? data : []
      },
    }),
    //  Get Subcategories List
    getBrandsList: builder.query<IBrandListResponse[], void>({
      query: () => ({
        url: 'getBrandList',
        method: 'GET',
        credentials: 'include',
      }),
      transformResponse: (response: any) => {
        const data = response?.data?.items
        return Array.isArray(data) ? data : []
      },
    }),

    //  Get Subcategories List
    getSubcategories: builder.query<any[], void>({
      query: () => ({
        url: 'getSubcategoryList',
        method: 'GET',
        credentials: 'include',
      }),
      transformResponse: (response: any) => {
        if (response?.status === 'success' && Array.isArray(response?.data?.items)) {
          return response.data.items
        }
        return []
      },
    }),

    // Get Dealer Groups
    getDealerGroups: builder.query<any[], void>({
      query: () => ({
        url: 'dealergroups/getDealerGroupsIds',
        method: 'GET',
        credentials: 'include',
      }),
      transformResponse: (response: any) => {
        const data = Array.isArray(response) ? response : []
        const sortedGroups = [...data].sort((a, b) => {
          const groupA = String(a.dealerGroupNumber || '').toLowerCase()
          const groupB = String(b.dealerGroupNumber || '').toLowerCase()
          return groupA.localeCompare(groupB)
        })
        return sortedGroups.map((group) => {
          const sortedDealers = [...(group.dealerIdList || [])].sort((a, b) => {
            const dealerA = String(a.dealerId || '').toLowerCase()
            const dealerB = String(b.dealerId || '').toLowerCase()
            return dealerA.localeCompare(dealerB)
          })
          return { ...group, dealerIdList: sortedDealers }
        })
      },
    }),

    deleteDealerSale: builder.mutation<
      GenericResponse,
      { dealerSalesId: number; rowData: SaleIntegrity }
    >({
      query({ dealerSalesId, rowData }) {
        return {
          url: `dealerSale/delete/${dealerSalesId}`,
          method: 'PUT',
          credentials: 'include',
          body: rowData,
        }
      },
      invalidatesTags: ['SaleIntegrity'],
    }),

    updateDealerSale: builder.mutation<GenericResponse, Partial<SaleIntegrity>>({
      query(data) {
        return {
          url: `/upload/uploadSale?dealerSalesId=${data.dealerSalesId}`,
          method: 'PUT',
          body: data,
          credentials: 'include',
        }
      },
      invalidatesTags: ['SaleIntegrity'],
    }),
    dealerSaleBulkUpdate: builder.mutation<IBulkUpdateResponse, IBulkUpdateRequest>({
      query: (body) => ({
        url: '/dealerSale/dealerSaleBulkUpdate',
        method: 'PUT',
        body,
        credentials: 'include',
      }),
      invalidatesTags: ['SaleIntegrity'],
    }),
    dealerSaleBulkDelete: builder.mutation<IBulkDeleteResponse, IBulkDeleteRequest>({
      query: (body) => ({
        url: '/dealerSale/dealerSaleBulkDelete',
        method: 'PUT',
        body,
        credentials: 'include',
      }),
      invalidatesTags: ['SaleIntegrity'],
    }),
    getSkuList: builder.query<ISkuItem[], string>({
      query: (retailer_code) => ({
        url: 'getSkuList',
        method: 'GET',
        params: { retailer_code },
        credentials: 'include',
      }),
      transformResponse: (response: any) => {
        const data = response?.data?.results
        return Array.isArray(data) ? data : []
      },
    }),
    reprocessSaleData: builder.mutation<IReprocessResponse, IReprocessRequest>({
      query: (body) => ({
        url: 'dealerSale/contractCreate',
        method: 'POST',
        body,
        credentials: 'include',
      }),
    }),
  }),
})

export const {
  useDeleteDealerSaleMutation,
  useGetDealerSaleQuery,
  useGetCountryListQuery,
  useGetStateListQuery,
  useGetBrandsListQuery,
  useGetSubcategoriesQuery,
  useGetDealerGroupsQuery,
  useUpdateDealerSaleMutation,
  useReprocessSaleDataMutation,
  useDealerSaleBulkUpdateMutation,
  useDealerSaleBulkDeleteMutation,
  useGetSkuListQuery,
} = SaleIntegrityApi
